import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { ArrowRight, Menu, X, Play, Layers, Download, Crop } from "lucide-react";

const LandingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const services = [
    { 
      icon: Crop,
      number: "01", 
      title: "Aspect Ratio",
      subtitle: "AI-Powered Resizing",
      description: "Transform your 16:9 content into 1:1, 9:16, 4:5 and custom formats with intelligent subject tracking.",
      price: "$4.99",
      link: "/crop-workspace"
    },
    { 
      icon: Layers,
      number: "02", 
      title: "Restripe",
      subtitle: "Graphics & Audio",
      description: "Update supers, disclaimers, end frames, and audio mixes with full timeline control.",
      price: "$2.99",
      link: "/dashboard"
    },
    { 
      icon: Download,
      number: "03", 
      title: "Mastering",
      subtitle: "Encode & Export",
      description: "Export to Facebook, Instagram, YouTube, TikTok, ProRes, and broadcast specifications.",
      price: "$0.99",
      link: "/dashboard"
    }
  ];

  return (
    <div className="min-h-screen" data-testid="landing-page">
      {/* Background with blue glow */}
      <div className="fixed inset-0 bg-gradient-hero -z-10" />
      
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'bg-[#020a14]/90 backdrop-blur-xl border-b border-cyan-500/10' : ''}`}>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center" data-testid="logo-link">
              <img src="/assets/logo.png" alt="MasteringMate" className="h-8" />
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-12">
              <a href="#services" className="text-sm text-cyan-200/60 hover:text-cyan-200 transition-colors">Services</a>
              <a href="#pricing" className="text-sm text-cyan-200/60 hover:text-cyan-200 transition-colors">Pricing</a>
              <a href="#about" className="text-sm text-cyan-200/60 hover:text-cyan-200 transition-colors">About</a>
            </div>

            {/* CTA */}
            <div className="hidden md:flex items-center gap-6">
              {user ? (
                <button 
                  onClick={() => navigate("/dashboard")}
                  className="text-sm flex items-center gap-2 text-cyan-300 hover:text-white transition-colors"
                  data-testid="dashboard-btn"
                >
                  Dashboard <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <>
                  <button 
                    onClick={() => navigate("/login")}
                    className="text-sm text-cyan-200/60 hover:text-cyan-200 transition-colors"
                    data-testid="login-btn"
                  >
                    Sign in
                  </button>
                  <button 
                    onClick={() => navigate("/register")}
                    className="text-sm flex items-center gap-2 px-5 py-2.5 btn-primary rounded-sm"
                    data-testid="get-started-btn"
                  >
                    Get Started <ArrowRight className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>

            {/* Mobile Menu Toggle */}
            <button 
              className="md:hidden p-2 text-cyan-300"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-[#020a14]/95 backdrop-blur-xl border-t border-cyan-500/10">
            <div className="px-6 py-8 space-y-6">
              <a href="#services" className="block text-2xl font-['Rajdhani'] text-cyan-200">Services</a>
              <a href="#pricing" className="block text-2xl font-['Rajdhani'] text-cyan-200">Pricing</a>
              <a href="#about" className="block text-2xl font-['Rajdhani'] text-cyan-200">About</a>
              <div className="pt-6 border-t border-cyan-500/20 space-y-4">
                {user ? (
                  <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-lg text-cyan-300">
                    Dashboard <ArrowRight className="w-4 h-4" />
                  </button>
                ) : (
                  <>
                    <button onClick={() => navigate("/login")} className="block text-cyan-200/60">Sign in</button>
                    <button onClick={() => navigate("/register")} className="flex items-center gap-2 text-lg text-cyan-300">
                      Get Started <ArrowRight className="w-4 h-4" />
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="min-h-screen flex flex-col justify-center pt-20 relative overflow-hidden">
        {/* Animated glow orb */}
        <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] rounded-full bg-cyan-500/20 blur-[150px] animate-pulse-glow" />
        
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 lg:py-32 relative z-10">
          <div className="max-w-4xl">
            <p className="text-sm text-cyan-400/80 mb-6 tracking-widest uppercase animate-fade-up">
              Video Post-Production Platform
            </p>
            <h1 className="text-[clamp(2.5rem,8vw,5rem)] font-['Rajdhani'] leading-[1] tracking-wide mb-8 animate-fade-up delay-100">
              THE FUTURE OF
              <br />
              <span className="text-cyan-400 glow-text">MASTERING</span> IS HERE
            </h1>
            <p className="text-lg text-cyan-100/50 max-w-xl leading-relaxed mb-12 animate-fade-up delay-200">
              Empower your team to resize, restripe, and render TVC videos. 
              No post house required. Just upload and go.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-up delay-300">
              <Button 
                onClick={() => navigate(user ? "/dashboard" : "/register")}
                className="h-14 px-8 rounded-sm btn-primary font-['Rajdhani'] text-base tracking-wider"
                data-testid="hero-cta-btn"
              >
                START FREE TRIAL
                <ArrowRight className="w-5 h-5 ml-3" />
              </Button>
              <Button 
                variant="outline"
                onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}
                className="h-14 px-8 rounded-sm btn-outline-blue font-['Rajdhani'] text-base tracking-wider"
              >
                EXPLORE SERVICES
              </Button>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-12 left-1/2 -translate-x-1/2">
          <div className="w-px h-20 bg-gradient-to-b from-cyan-500/50 via-cyan-400/30 to-transparent animate-pulse" />
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-32 lg:py-48 relative">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="mb-20">
            <p className="text-sm text-cyan-400/80 mb-4 tracking-widest uppercase">Our Services</p>
            <h2 className="text-3xl md:text-4xl font-['Rajdhani'] tracking-wide">
              THREE POWERFUL TOOLS
              <br />
              <span className="text-cyan-400/50">INFINITE POSSIBILITIES</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <div 
                key={service.number}
                className="group card-glass rounded-sm p-8 cursor-pointer"
                onClick={() => navigate(user ? service.link : "/login")}
                data-testid={`service-${index}`}
              >
                <div className="flex items-center justify-between mb-6">
                  <service.icon className="w-8 h-8 text-cyan-400" />
                  <span className="text-sm text-cyan-500/40 font-mono">{service.number}</span>
                </div>
                <h3 className="text-2xl font-['Rajdhani'] tracking-wide mb-2 group-hover:text-cyan-300 transition-colors">
                  {service.title}
                </h3>
                <p className="text-sm text-cyan-400/60 mb-4">{service.subtitle}</p>
                <p className="text-cyan-100/40 text-sm leading-relaxed mb-6">
                  {service.description}
                </p>
                <div className="pt-4 border-t border-cyan-500/10 flex items-center justify-between">
                  <div>
                    <span className="text-2xl font-['Rajdhani'] text-cyan-300">{service.price}</span>
                    <span className="text-sm text-cyan-500/40 ml-2">per edit</span>
                  </div>
                  <span className="text-sm text-cyan-400/40 group-hover:text-cyan-300 flex items-center gap-1 transition-colors">
                    Open <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature Section */}
      <section className="py-32 lg:py-48 relative">
        <div className="absolute inset-0 bg-gradient-radial opacity-50" />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 relative">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            <div>
              <p className="text-sm text-cyan-400/80 mb-6 tracking-widest uppercase">Why MasteringMate</p>
              <h2 className="text-3xl md:text-4xl font-['Rajdhani'] tracking-wide leading-tight mb-8">
                POST HOUSES WERE
                <br />
                BUILT IN THE FTA ERA
              </h2>
              <p className="text-lg text-cyan-100/40 leading-relaxed mb-8">
                Let the technicals focus on their craft. MasteringMate handles the mastering side — 
                at a fraction of the cost, with zero compromise on quality.
              </p>
              <ul className="space-y-4">
                {["AI-powered automation", "24/7 availability", "Real-time collaboration", "Enterprise-grade security"].map((item, i) => (
                  <li key={i} className="flex items-center gap-4 text-cyan-200/60">
                    <span className="w-2 h-2 rounded-full bg-cyan-400" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="aspect-square card-glass rounded-sm flex items-center justify-center relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-radial opacity-30" />
              <span className="text-[10rem] font-['Rajdhani'] font-bold text-cyan-500/10 glow-text">M</span>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-32 lg:py-48">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-20">
            <p className="text-sm text-cyan-400/80 mb-4 tracking-widest uppercase">Simple Pricing</p>
            <h2 className="text-3xl md:text-4xl font-['Rajdhani'] tracking-wide">
              PAY PER USE. NO SUBSCRIPTIONS.
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-px bg-cyan-500/10 max-w-4xl mx-auto rounded-sm overflow-hidden">
            {services.map((service, index) => (
              <div 
                key={service.number}
                className="bg-[#020a14] p-10 lg:p-12"
                data-testid={`pricing-${index}`}
              >
                <p className="text-sm text-cyan-400/60 mb-4">{service.subtitle}</p>
                <h3 className="text-xl font-['Rajdhani'] tracking-wide mb-2">{service.title}</h3>
                <div className="mt-8">
                  <span className="text-4xl font-['Rajdhani'] text-cyan-300">{service.price}</span>
                </div>
                <p className="text-sm text-cyan-500/40 mt-2">per edit</p>
              </div>
            ))}
          </div>

          <p className="text-center text-sm text-cyan-500/30 mt-12">
            First encode free when using Aspect Ratio or Restripe
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 lg:py-48 relative">
        <div className="absolute inset-0 bg-gradient-radial" />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 text-center relative">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-['Rajdhani'] tracking-wide mb-8">
            READY TO START?
          </h2>
          <p className="text-lg text-cyan-100/40 mb-12 max-w-xl mx-auto">
            Join agencies who trust MasteringMate for their video post production.
          </p>
          <Button 
            onClick={() => navigate(user ? "/dashboard" : "/register")}
            className="h-14 px-10 rounded-sm btn-primary font-['Rajdhani'] text-base tracking-wider animate-pulse-glow"
            data-testid="cta-btn"
          >
            GET STARTED FREE
            <ArrowRight className="w-5 h-5 ml-3" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer id="about" className="border-t border-cyan-500/10 py-12">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <img src="/assets/logo.png" alt="MasteringMate" className="h-5 opacity-60" />
            <p className="text-sm text-cyan-500/30">© 2026 MasteringMate. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
